/**
 * WorkspaceDashboard — redesigned overview (EM design handoff).
 * KPI strip + Active Projects panel + My Tasks panel, wired to real APIs.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FolderKanban, ListChecks, Timer, Building2, TrendingUp, ArrowRight,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { wsApi, wsBase, isManager } from './wsApi';
import { T, Select } from '../../components/em/ui';
import { Skeleton } from './wsUi';
import { PRIORITY, fmtDate } from './wsConstants';

const isoDate = (d) => { const tz = d.getTimezoneOffset() * 60000; return new Date(d - tz).toISOString().slice(0, 10); };

// Working-hours graph with employee / project / team filters (Overview).
function HoursGraph({ projects }) {
  const manager = isManager();
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [from] = useState(isoDate(new Date(Date.now() - 29 * 864e5)));
  const [to] = useState(isoDate(new Date()));

  useEffect(() => {
    if (manager) {
      wsApi.users().then(setUsers).catch(() => {});
      wsApi.listTeams().then(setTeams).catch(() => {});
    }
  }, [manager]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    p.set('from', from); p.set('to', to);
    if (manager && userId) p.set('user_id', userId);
    if (projectId) p.set('project_id', projectId);
    if (teamId) p.set('team_id', teamId);
    wsApi.hoursGraph(`?${p.toString()}`)
      .then((rows) => setData((rows || []).map((r) => ({ ...r, label: fmtDate(r.date).replace(/,.*$/, '') }))))
      .catch(() => {});
  }, [from, to, userId, projectId, teamId, manager]);

  useEffect(() => { load(); }, [load]);

  // Compact variant of the shared Select (which supplies the dropdown chevron).
  // flex:1 + minWidth:0 keeps the filters side by side in a single row and lets
  // them share the width instead of wrapping onto separate lines.
  const sel = { flex: 1, minWidth: 0, padding: '7px 26px 7px 10px', borderRadius: 8, font: `500 12px ${T.font}`, color: T.fg2 };

  return (
    <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadowSm, marginBottom: 18 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ font: `700 15px ${T.font}`, color: T.fg1, marginBottom: 10 }}>Working hours · last 30 days</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {manager && (
            <Select style={sel} value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">All employees</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
            </Select>
          )}
          <Select style={sel} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          {manager && (
            <Select style={sel} value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              <option value="">All teams</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          )}
        </div>
      </div>
      {data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: T.fg7, font: `400 13px ${T.font}` }}>No hours logged in this range.</div>
      ) : (
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={data} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--em-border)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--em-fg5)' }} tickLine={false} axisLine={false} minTickGap={16} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--em-fg5)' }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid var(--em-border)', fontSize: 12, background: 'var(--em-bg-card)', color: 'var(--em-fg1)' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="total_hours" name="Total" fill="#f54b01" radius={[4, 4, 0, 0]} />
              <Bar dataKey="billable_hours" name="Billable" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function KpiCard({ Icon, label, value, sub, color, bg }) {
  return (
    <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadowSm }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <Icon size={18} color={color} />
      </div>
      <div style={{ font: `800 26px/1 ${T.font}`, letterSpacing: '-0.03em', color: T.fg1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ font: `500 12px ${T.font}`, color: T.fg4, marginTop: 6 }}>{label}</div>
      {sub != null && <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Panel({ title, action, children }) {
  return (
    <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadowSm }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ font: `700 15px ${T.font}`, color: T.fg1, flex: 1 }}>{title}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function WorkspaceDashboard() {
  const navigate = useNavigate();
  const manager = isManager();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([wsApi.dashboard(), wsApi.myTasks(), wsApi.listProjects()])
      .then(([s, t, p]) => {
        setStats(s);
        setTasks((t || []).filter((x) => x.status !== 'done').slice(0, 6));
        setProjects((p || []).filter((x) => !['completed', 'archived'].includes(x.status)).slice(0, 6));
      })
      .catch(() => toast.error('Could not load workspace dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 18 }}>
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} h={130} radius={14} />)}
      </div>
      <Skeleton h={200} radius={14} style={{ marginBottom: 18 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 16 }}>
        <Skeleton h={320} radius={14} />
        <Skeleton h={320} radius={14} />
      </div>
    </div>
  );

  const link = (to) => (
    <button onClick={() => navigate(`${wsBase()}/${to}`)} style={{ background: 'transparent', border: 0, color: T.orange, font: `600 12px ${T.font}`, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      View all <ArrowRight size={13} />
    </button>
  );

  const kpis = [
    { label: 'Active Projects', value: stats?.active_projects ?? 0, sub: 'in progress', Icon: FolderKanban, color: T.orange, bg: T.orange50 },
    { label: 'My Open Tasks', value: stats?.my_open_tasks ?? 0, sub: 'assigned to you', Icon: ListChecks, color: T.info600, bg: T.info50 },
    { label: 'My Hours (month)', value: `${stats?.my_hours_this_month ?? 0}h`, sub: 'logged this month', Icon: Timer, color: T.success600, bg: T.success50 },
    manager
      ? { label: 'Billable Hours', value: `${stats?.billable_hours_this_month ?? 0}h`, sub: `${stats?.active_clients ?? 0} active clients`, Icon: TrendingUp, color: T.purple, bg: T.purple50 }
      : { label: 'Active Clients', value: stats?.active_clients ?? 0, sub: 'engaged', Icon: Building2, color: T.purple, bg: T.purple50 },
  ];

  return (
    <div>
      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 18 }}>
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      {/* Working-hours graph */}
      <HoursGraph projects={projects} />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 16 }}>
        {/* Active projects */}
        <Panel title="Active Projects" action={link('projects')}>
          {projects.length === 0 ? (
            <div style={{ font: `400 13px ${T.font}`, color: T.fg7, padding: '12px 0' }}>No active projects yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {projects.map((p) => {
                const pct = p.task_count > 0 ? Math.round((p.done_count / p.task_count) * 100) : 0;
                return (
                  <div key={p.id} onClick={() => navigate(`${wsBase()}/projects/${p.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 10px', borderRadius: 10, cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = T.bgHead)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: p.color || T.orange, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: `600 13px ${T.font}`, color: T.fg1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                      <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 2 }}>{p.client_name || 'No client'} · {p.task_count} tasks</div>
                    </div>
                    <div style={{ width: 120, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 6, borderRadius: 9999, background: T.bgMuted, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: p.color || T.orange, borderRadius: 9999 }} />
                      </div>
                      <span style={{ font: `600 11px ${T.font}`, color: T.fg4, width: 30, textAlign: 'right' }}>{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        {/* My tasks */}
        <Panel title="My Tasks" action={(
          <button onClick={() => navigate(`${wsBase()}/tasks`)} style={{ background: 'transparent', border: 0, color: T.orange, font: `600 12px ${T.font}`, cursor: 'pointer' }}>All</button>
        )}>
          {tasks.length === 0 ? (
            <div style={{ font: `400 12px ${T.font}`, color: T.fg7, padding: '8px 0' }}>Nothing assigned — nice. 🌤️</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tasks.map((t) => {
                const pr = PRIORITY[t.priority] || PRIORITY.medium;
                const overdue = t.due_date && new Date(t.due_date) < new Date();
                return (
                  <div key={t.id} onClick={() => navigate(`${wsBase()}/tasks`)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <span style={{ width: 7, height: 7, borderRadius: 9999, background: pr.text, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0, font: `500 12px ${T.font}`, color: T.fg1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</div>
                    {t.due_date && <span style={{ font: `500 11px ${T.font}`, color: overdue ? T.danger : T.fg7, whiteSpace: 'nowrap' }}>{fmtDate(t.due_date)}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
